import { Content } from "@/ui/Content";
import { Collection } from "@sourceflow-uk/sourceflow-sdk";
import { getInsights } from "@/functions/getInsights";
import { insights_helper } from "@/helpers/insights_helper";
import * as additionalComponents from "@/components/InsightPage";
import BlogPostingSchema from "@/ui/BlogPostingSchema";
import { parseRoute } from "@/functions/parseRoute";
import { trimText } from "@/functions/trimText";
import sourceflowEnums from "@/sourceflow.enums";
import { format } from "date-fns";
import { useLocale } from "@/hooks/useLocale";
import { useLink } from "@/hooks/useLink";

export default function Page({ content, item }) {
  const locale = useLocale();
  const link = useLink();

  return (
    <>
      <BlogPostingSchema
        title={item[`title_${locale}`]}
        url={link("blog.item", item.url_slug)}
        description={trimText(item[`text_${locale}`], 155)}
        image={item.image}
        author={item.author}
        datePublished={item.published ? format(new Date(item.published), "yyyy-MM-dd") : null}
        dateModified={item.updated_at ? format(new Date(item.updated_at), "yyyy-MM-dd") : null}
        publisherName={sourceflowEnums.config.siteName}
        publisherLogo={sourceflowEnums.config.socialLogo}
      />
      <Content items={content} additionalComponents={additionalComponents} />
    </>
  );
}

export async function getStaticProps({ params: { url_slug } }) {
  const item = getInsights().find((i) => i.url_slug === url_slug || i["url_nl"] === url_slug);

  const routerKey = "blog.item";
  const routerProp = parseRoute(routerKey, { url_slug });

  // Article bodies are the bulk of each record and the rail never reads them.
  const related = insights_helper.fetch({ limit: 3, exclude: [item.id] }).map((i) =>
    Object.fromEntries(Object.entries(i).filter(([key]) => !key.startsWith("text_")))
  );

  return {
    props: {
      meta: {
        en: {
          title: item["title_en"],
          description: trimText(item[`text_en`], 155),
          canonical: `${sourceflowEnums.config.baseUrl}${routerProp["en"]}`,
          images: [{ url: item?.image ?? sourceflowEnums.config.socialLogo }],
          titleTemplate: "",
        },
      },
      item,
      router: routerProp,
      content: [
        { component: "InsightPageBannerSection", props: { className: "", item } },
        { component: "InsightPageContentSection", props: { className: "", item } },
        { component: "InsightPageRelatedSection", props: { className: "py-4 py-md-5 mb-md-5", item, related } },
      ],
    },
  };
}

export async function getStaticPaths() {
  return {
    paths: (await Collection("insights"))
      .getItems()
      .map((i) => ({ params: { url_slug: "url_nl" in i ? i["url_nl"] : i.url_slug } })),
    fallback: false,
  };
}
